import { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  ExternalLink,
  FolderOpen,
  HardDrive,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  Video,
} from 'lucide-react';
import { api } from '../../services/api';
import toast from '../Toast';
import { formatBeijingDateTime } from '../../utils/time';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';
import type {
  StorageBrowseScope,
  StorageObjectItem,
  StorageObjectListResult,
  StoragePrefixNode,
  StoragePrefixTreeResult,
} from './types';

const PAGE_SIZE = 50;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function normalizePrefix(prefix: string): string {
  const value = prefix.trim();
  if (!value) return '';
  return value.endsWith('/') ? value : `${value}/`;
}

function buildBreadcrumbs(prefix: string): Array<{ label: string; prefix: string }> {
  const normalized = normalizePrefix(prefix);
  if (!normalized) return [];
  const parts = normalized.split('/').filter(Boolean);
  return parts.map((part, index) => ({
    label: part,
    prefix: `${parts.slice(0, index + 1).join('/')}/`,
  }));
}

export default function StorageBucketTab() {
  const [scope, setScope] = useState<StorageBrowseScope>('all');
  const [prefixInput, setPrefixInput] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [unreferencedOnly, setUnreferencedOnly] = useState(false);
  const [treeResult, setTreeResult] = useState<StoragePrefixTreeResult | null>(null);
  const [objectResult, setObjectResult] = useState<StorageObjectListResult | null>(null);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<Array<string | null>>([]);
  const [loadingTree, setLoadingTree] = useState(false);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [previewItem, setPreviewItem] = useState<StorageObjectItem | null>(null);

  const items = objectResult?.data || [];
  const treeNodes = treeResult?.data || [];
  const selectedOnPageCount = items.filter((item) => selectedKeys.has(item.key)).length;
  const allVisibleSelected = items.length > 0 && selectedOnPageCount === items.length;
  const nextCursor = objectResult?.meta.nextCursor ?? null;
  const canUseReferenceFilter = currentPrefix.startsWith('videos/') || currentPrefix.startsWith('images/');
  const breadcrumbs = useMemo(() => buildBreadcrumbs(currentPrefix), [currentPrefix]);

  const appliedHint = useMemo(() => {
    if (!currentPrefix) return '左侧先选目录，再在右侧浏览对象。全部范围下根目录只展示目录树，不直接翻全桶。';
    const scopeHint = unreferencedOnly ? '仅显示未被媒体账本或任务记录引用的对象。' : '支持预览、复制链接和删除。';
    return `当前目录：${currentPrefix}，${scopeHint}`;
  }, [currentPrefix, unreferencedOnly]);

  const loadTree = async (nextScope: StorageBrowseScope, prefix: string) => {
    setLoadingTree(true);
    try {
      const data = await api.getAdminStorageTree({
        scope: nextScope,
        prefix,
      });
      setTreeResult(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载目录树失败');
    } finally {
      setLoadingTree(false);
    }
  };

  const loadObjects = async (params?: {
    scope?: StorageBrowseScope;
    prefix?: string;
    search?: string;
    cursor?: string | null;
    resetHistory?: boolean;
    unreferencedOnly?: boolean;
  }) => {
    const nextScope = params?.scope ?? scope;
    const nextPrefix = params?.prefix ?? currentPrefix;
    const nextSearch = params?.search ?? searchInput;
    const nextCursorValue = params?.cursor ?? null;
    const nextUnreferencedOnly = params?.unreferencedOnly ?? unreferencedOnly;

    setLoadingObjects(true);
    try {
      const data = await api.getAdminStorageObjects({
        scope: nextScope,
        prefix: nextPrefix,
        search: nextSearch.trim(),
        cursor: nextCursorValue,
        limit: PAGE_SIZE,
        unreferencedOnly: nextUnreferencedOnly,
      });
      setObjectResult(data);
      setCurrentCursor(nextCursorValue);
      if (params?.resetHistory) setCursorHistory([]);
      setSelectedKeys(new Set());
      setPreviewItem((prev) => data.data.find((item) => item.key === prev?.key) || data.data.find((item) => item.previewType === 'image' || item.previewType === 'video') || null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '加载存储对象失败');
    } finally {
      setLoadingObjects(false);
    }
  };

  const syncView = async (nextScope: StorageBrowseScope, nextPrefix: string, options?: {
    search?: string;
    cursor?: string | null;
    resetHistory?: boolean;
    unreferencedOnly?: boolean;
  }) => {
    await Promise.all([
      loadTree(nextScope, nextPrefix),
      loadObjects({
        scope: nextScope,
        prefix: nextPrefix,
        search: options?.search,
        cursor: options?.cursor ?? null,
        resetHistory: options?.resetHistory,
        unreferencedOnly: options?.unreferencedOnly,
      }),
    ]);
  };

  useEffect(() => {
    void syncView('all', '', { resetHistory: true, unreferencedOnly: false });
  }, []);

  const handleScopeChange = async (nextScope: StorageBrowseScope) => {
    setScope(nextScope);
    setUnreferencedOnly(false);
    if (nextScope === 'videos') {
      setCurrentPrefix('videos/');
      setPrefixInput('videos/');
      await syncView(nextScope, 'videos/', { resetHistory: true, unreferencedOnly: false });
      return;
    }
    if (nextScope === 'images') {
      setCurrentPrefix('images/');
      setPrefixInput('images/');
      await syncView(nextScope, 'images/', { resetHistory: true, unreferencedOnly: false });
      return;
    }
    if (nextScope === 'all') {
      setCurrentPrefix('');
      setPrefixInput('');
      await syncView(nextScope, '', { resetHistory: true, unreferencedOnly: false });
      return;
    }
    setCurrentPrefix(prefixInput.trim());
  };

  const enterPrefix = async (nextPrefix: string) => {
    const normalized = normalizePrefix(nextPrefix);
    setCurrentPrefix(normalized);
    setPrefixInput(normalized);
    await syncView(scope, normalized, { resetHistory: true });
  };

  const goParentPrefix = async () => {
    const parent = treeResult?.meta.parentPrefix || '';
    setCurrentPrefix(parent);
    setPrefixInput(parent);
    await syncView(scope, parent, { resetHistory: true });
  };

  const toggleSelect = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectCurrentPage = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const item of items) next.delete(item.key);
      } else {
        for (const item of items) next.add(item.key);
      }
      return next;
    });
  };

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('已复制');
    } catch {
      toast.error('复制失败');
    }
  };

  const handleDelete = async (keys: string[]) => {
    if (keys.length === 0) {
      toast.error('请先选择对象');
      return;
    }
    if (!window.confirm(`确定删除选中的 ${keys.length} 个对象吗？该操作不可恢复。`)) {
      return;
    }

    setDeleting(true);
    try {
      const result = await api.deleteAdminStorageObjects(keys);
      toast.success(result.message);
      await syncView(scope, currentPrefix, {
        search: searchInput,
        cursor: currentCursor,
        unreferencedOnly,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  const handleSearch = async () => {
    await syncView(scope, currentPrefix, {
      search: searchInput,
      cursor: null,
      resetHistory: true,
      unreferencedOnly,
    });
  };

  const handleManualPrefixJump = async () => {
    const normalized = normalizePrefix(prefixInput);
    setCurrentPrefix(normalized);
    await syncView(scope, normalized, {
      search: searchInput,
      cursor: null,
      resetHistory: true,
      unreferencedOnly,
    });
  };

  const handleNextPage = async () => {
    if (!nextCursor) return;
    setCursorHistory((prev) => [...prev, currentCursor]);
    await loadObjects({
      scope,
      prefix: currentPrefix,
      search: searchInput,
      cursor: nextCursor,
      unreferencedOnly,
    });
  };

  const handlePrevPage = async () => {
    if (cursorHistory.length === 0) return;
    const previous = cursorHistory[cursorHistory.length - 1];
    setCursorHistory((prev) => prev.slice(0, -1));
    await loadObjects({
      scope,
      prefix: currentPrefix,
      search: searchInput,
      cursor: previous,
      unreferencedOnly,
    });
  };

  const handleUnreferencedToggle = async (checked: boolean) => {
    setUnreferencedOnly(checked);
    await syncView(scope, currentPrefix, {
      search: searchInput,
      cursor: null,
      resetHistory: true,
      unreferencedOnly: checked,
    });
  };

  const renderTreeNode = (node: StoragePrefixNode) => (
    <button
      key={node.prefix}
      onClick={() => void enterPrefix(node.prefix)}
      className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition-colors ${
        currentPrefix === node.prefix ? 'bg-accent-blue/10 text-accent-blue' : 'hover:bg-light-hover text-light-text'
      }`}
    >
      <span className="flex items-center gap-2 truncate">
        <FolderOpen className="h-4 w-4 shrink-0" />
        <span className="truncate font-mono text-xs">{node.prefix}</span>
      </span>
      <span className="text-[11px] uppercase text-light-muted">{node.namespace}</span>
    </button>
  );

  const renderPreview = () => {
    if (!previewItem || !previewItem.accessUrl) {
      return <div className="text-sm text-light-muted">当前没有可预览对象</div>;
    }

    if (previewItem.previewType === 'image') {
      return (
        <img
          src={resolveBrowserMediaUrl(previewItem.accessUrl)}
          alt={previewItem.key}
          className="max-h-[360px] w-full rounded-xl border border-light-border object-contain"
        />
      );
    }

    if (previewItem.previewType === 'video') {
      return (
        <video
          src={resolveBrowserMediaUrl(previewItem.accessUrl)}
          controls
          className="max-h-[360px] w-full rounded-xl border border-light-border bg-black"
        />
      );
    }

    return <div className="text-sm text-light-muted">该对象类型不支持直接预览</div>;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-light-border bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-light-text">MinIO 存储桶管理</h3>
            <p className="text-sm text-light-muted">{appliedHint}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => void syncView(scope, currentPrefix, { search: searchInput, cursor: currentCursor, unreferencedOnly })}
              disabled={loadingTree || loadingObjects || deleting}
              className="inline-flex items-center gap-2 rounded-xl bg-light-hover px-4 py-2 text-sm font-medium text-light-text transition-colors hover:bg-light-border disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loadingTree || loadingObjects ? 'animate-spin' : ''}`} />
              刷新
            </button>
            <button
              onClick={() => void handleDelete(Array.from(selectedKeys))}
              disabled={selectedKeys.size === 0 || loadingObjects || deleting}
              className="inline-flex items-center gap-2 rounded-xl bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Trash2 className="h-4 w-4" />
              删除已选
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-4">
          <label className="space-y-1">
            <span className="text-sm font-medium text-light-text">查看范围</span>
            <select
              value={scope}
              onChange={(event) => void handleScopeChange(event.target.value as StorageBrowseScope)}
              className="input-field w-full"
            >
              <option value="all">MinIO 媒体根目录</option>
              <option value="videos">仅 videos/</option>
              <option value="images">仅 images/</option>
              <option value="custom">自定义前缀</option>
            </select>
          </label>

          <label className="space-y-1 lg:col-span-2">
            <span className="text-sm font-medium text-light-text">前缀跳转</span>
            <input
              value={prefixInput}
              onChange={(event) => setPrefixInput(event.target.value)}
              className="input-field w-full font-mono text-sm"
              placeholder={scope === 'custom' ? '例如 videos/0753c702/' : '例如 videos/ 或 images/'}
            />
          </label>

          <div className="flex items-end">
            <button
              onClick={() => void handleManualPrefixJump()}
              disabled={loadingTree || loadingObjects}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-accent-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-blue/90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FolderOpen className="h-4 w-4" />
              跳转目录
            </button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-4">
          <label className="space-y-1 lg:col-span-2">
            <span className="text-sm font-medium text-light-text">关键字</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-light-muted" />
              <input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="input-field w-full pl-10"
                placeholder="按 key 搜索；搜索时会退回当前 prefix 全量过滤"
              />
            </div>
          </label>

          <label className="flex items-center gap-3 rounded-xl border border-light-border px-3 py-2 text-sm text-light-text">
            <input
              type="checkbox"
              checked={unreferencedOnly}
              disabled={!canUseReferenceFilter}
              onChange={(event) => void handleUnreferencedToggle(event.target.checked)}
              className="h-4 w-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
            />
仅未被媒体记录引用
          </label>

          <div className="flex items-end">
            <button
              onClick={() => void handleSearch()}
              disabled={loadingObjects}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-light-hover px-4 py-2 text-sm font-medium text-light-text transition-colors hover:bg-light-border disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loadingObjects ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              查询对象
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[280px_minmax(0,1fr)_320px]">
        <div className="rounded-2xl border border-light-border bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-light-border px-4 py-4">
            <div className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-light-muted" />
              <div>
                <h3 className="text-base font-semibold text-light-text">目录树</h3>
                <p className="text-xs text-light-muted">按 prefix 浏览</p>
              </div>
            </div>
            <button
              onClick={() => void loadTree(scope, currentPrefix)}
              disabled={loadingTree}
              className="rounded-lg p-2 transition-colors hover:bg-light-hover disabled:opacity-40"
            >
              <RefreshCw className={`h-4 w-4 text-light-text ${loadingTree ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="space-y-3 px-4 py-4">
            {breadcrumbs.length > 0 && (
              <div className="rounded-xl bg-light-hover p-3">
                <div className="mb-2 text-xs font-medium text-light-muted">当前位置</div>
                <div className="flex flex-wrap gap-2">
                  {breadcrumbs.map((item) => (
                    <button
                      key={item.prefix}
                      onClick={() => void enterPrefix(item.prefix)}
                      className="rounded-lg bg-white px-2 py-1 font-mono text-xs text-light-text shadow-sm transition-colors hover:bg-light-hover"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {treeResult?.meta.parentPrefix !== null && (
              <button
                onClick={() => void goParentPrefix()}
                className="inline-flex items-center gap-2 rounded-xl bg-light-hover px-3 py-2 text-sm text-light-text transition-colors hover:bg-light-border"
              >
                <ChevronLeft className="h-4 w-4" />
                返回上级目录
              </button>
            )}

            {loadingTree ? (
              <div className="flex items-center justify-center py-10 text-sm text-light-muted">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                目录加载中
              </div>
            ) : treeNodes.length === 0 ? (
              <div className="rounded-xl border border-dashed border-light-border px-4 py-6 text-center text-sm text-light-muted">
                当前目录下没有子目录
              </div>
            ) : (
              <div className="space-y-2">{treeNodes.map(renderTreeNode)}</div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-light-border bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-light-border px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <HardDrive className="h-5 w-5 text-light-muted" />
              <div>
                <h3 className="text-base font-semibold text-light-text">对象列表</h3>
                <p className="text-sm text-light-muted">
                  当前显示 {items.length} 个对象，已选 {selectedKeys.size} 个
                </p>
              </div>
            </div>
            {items.length > 0 && (
              <button
                onClick={toggleSelectCurrentPage}
                className="rounded-lg bg-light-hover px-3 py-1.5 text-sm text-light-text transition-colors hover:bg-light-border"
              >
                {allVisibleSelected ? '取消本页全选' : '全选本页'}
              </button>
            )}
          </div>

          {!currentPrefix && scope === 'all' ? (
            <div className="px-4 py-16 text-center text-sm text-light-muted">请先从左侧选择 `videos/` 或 `images/` 目录</div>
          ) : loadingObjects ? (
            <div className="flex items-center justify-center py-16 text-light-muted">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              对象加载中
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-16 text-center text-sm text-light-muted">当前 prefix 下没有找到对象</div>
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full min-w-[980px] text-sm">
                  <thead className="bg-gray-50 text-left text-gray-600">
                    <tr>
                      <th className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectCurrentPage}
                          className="h-4 w-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                        />
                      </th>
                      <th className="px-4 py-3 font-medium">Key</th>
                      <th className="px-4 py-3 font-medium">预览</th>
                      <th className="px-4 py-3 font-medium">引用状态</th>
                      <th className="px-4 py-3 font-medium">大小</th>
                      <th className="px-4 py-3 font-medium">最后修改</th>
                      <th className="px-4 py-3 font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {items.map((item) => (
                      <tr key={item.key} className="hover:bg-gray-50/70">
                        <td className="px-4 py-3 align-top">
                          <input
                            type="checkbox"
                            checked={selectedKeys.has(item.key)}
                            onChange={() => toggleSelect(item.key)}
                            className="mt-1 h-4 w-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                          />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ObjectKeyCell item={item} onPreview={setPreviewItem} />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <PreviewTypePill previewType={item.previewType} />
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ReferenceStatus item={item} />
                        </td>
                        <td className="px-4 py-3 align-top text-light-text">{formatBytes(item.size)}</td>
                        <td className="px-4 py-3 align-top text-light-text">
                          {formatDateTime(item.lastModified)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ObjectActions
                            item={item}
                            deleting={deleting}
                            onCopyText={copyText}
                            onDelete={handleDelete}
                            mobile={false}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="divide-y divide-light-border lg:hidden">
                {items.map((item) => (
                  <div key={item.key} className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedKeys.has(item.key)}
                        onChange={() => toggleSelect(item.key)}
                        className="mt-1 h-4 w-4 rounded border-light-border text-accent-blue focus:ring-accent-blue"
                      />
                      <div className="min-w-0 flex-1 space-y-3">
                        <ObjectKeyCell item={item} onPreview={setPreviewItem} />
                        <div className="flex flex-wrap gap-2">
                          <PreviewTypePill previewType={item.previewType} />
                          <ReferenceStatus item={item} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <InfoTile label="大小" value={formatBytes(item.size)} />
                          <InfoTile label="最后修改" value={formatDateTime(item.lastModified)} />
                        </div>
                        <ObjectActions
                          item={item}
                          deleting={deleting}
                          onCopyText={copyText}
                          onDelete={handleDelete}
                          mobile
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-3 border-t border-light-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-light-muted">
                  当前游标 {currentCursor ? '已前进' : '起始页'}，下一页 {nextCursor ? '可继续加载' : '已到末尾'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => void handlePrevPage()}
                    disabled={cursorHistory.length === 0 || loadingObjects}
                    className="rounded-lg p-2 transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronLeft className="h-4 w-4 text-light-text" />
                  </button>
                  <button
                    onClick={() => void handleNextPage()}
                    disabled={!nextCursor || loadingObjects}
                    className="rounded-lg p-2 transition-colors hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4 text-light-text" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-light-border bg-white shadow-sm">
          <div className="border-b border-light-border px-4 py-4">
            <h3 className="text-base font-semibold text-light-text">预览面板</h3>
            <p className="text-sm text-light-muted">支持图片和视频预览</p>
          </div>
          <div className="space-y-4 px-4 py-4">
            {renderPreview()}
            {previewItem && (
              <div className="space-y-2 rounded-xl bg-light-hover p-3 text-sm">
                <div className="break-all font-mono text-xs text-light-text">{previewItem.key}</div>
                <div className="text-light-muted">类型：{previewItem.contentType}</div>
                <div className="text-light-muted">大小：{formatBytes(previewItem.size)}</div>
                <div className="text-light-muted">
                  引用状态：
                  {previewItem.isReferenced ? ' 已被媒体记录引用' : ' 未被媒体记录引用'}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => copyText(previewItem.key)}
                    className="rounded-lg bg-white px-3 py-1.5 text-xs text-light-text shadow-sm transition-colors hover:bg-light-border"
                  >
                    复制 key
                  </button>
                  <button
                    onClick={() => previewItem.accessUrl && window.open(resolveBrowserMediaUrl(previewItem.accessUrl), '_blank', 'noopener,noreferrer')}
                    disabled={!previewItem.accessUrl}
                    className="rounded-lg bg-white px-3 py-1.5 text-xs text-light-text shadow-sm transition-colors hover:bg-light-border disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    打开原文件
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(value: string): string {
  return formatBeijingDateTime(value);
}

function ObjectKeyCell({
  item,
  onPreview,
}: {
  item: StorageObjectItem;
  onPreview: (item: StorageObjectItem) => void;
}) {
  return (
    <>
      <button
        onClick={() => onPreview(item)}
        className="max-w-[420px] break-all text-left font-mono text-xs text-light-text hover:text-accent-blue"
      >
        {item.key}
      </button>
      <div className="mt-1 text-xs text-light-muted">
MinIO · {item.contentType}
      </div>
    </>
  );
}

function PreviewTypePill({ previewType }: { previewType: StorageObjectItem['previewType'] }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-light-hover px-2.5 py-1 text-xs text-light-text">
      {previewType === 'image' ? (
        <ImageIcon className="h-3.5 w-3.5" />
      ) : previewType === 'video' ? (
        <Video className="h-3.5 w-3.5" />
      ) : (
        <HardDrive className="h-3.5 w-3.5" />
      )}
      {previewType}
    </div>
  );
}

function ReferenceStatus({ item }: { item: StorageObjectItem }) {
  if (item.isReferenced) {
    return <span className="rounded-full bg-green-50 px-2.5 py-1 text-xs text-green-700">已被媒体记录引用</span>;
  }
  return <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-700">未被媒体记录引用</span>;
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-light-hover p-3">
      <div className="text-xs text-light-muted">{label}</div>
      <div className="mt-1 break-all text-sm text-light-text">{value}</div>
    </div>
  );
}

function ObjectActions({
  item,
  deleting,
  onCopyText,
  onDelete,
  mobile,
}: {
  item: StorageObjectItem;
  deleting: boolean;
  onCopyText: (value: string) => Promise<void>;
  onDelete: (keys: string[]) => Promise<void>;
  mobile: boolean;
}) {
  const baseClass = mobile
    ? 'inline-flex items-center justify-center gap-1 rounded-lg border border-light-border px-3 py-2 text-xs text-light-text hover:bg-light-hover'
    : 'rounded-lg p-2 text-light-muted transition-colors hover:bg-light-hover hover:text-light-text';
  return (
    <div className={mobile ? 'flex flex-wrap gap-2' : 'flex items-center gap-2'}>
      <button onClick={() => void onCopyText(item.key)} className={baseClass} title="复制 key">
        <Copy className="h-4 w-4" />
        {mobile && '复制 key'}
      </button>
      <button
        onClick={() => item.accessUrl && void onCopyText(resolveBrowserMediaUrl(item.accessUrl))}
        disabled={!item.accessUrl}
        className={mobile
          ? 'inline-flex items-center justify-center gap-1 rounded-lg border border-light-border px-3 py-2 text-xs text-light-text hover:bg-light-hover disabled:cursor-not-allowed disabled:opacity-40'
          : 'rounded-lg p-2 text-light-muted transition-colors hover:bg-light-hover hover:text-light-text disabled:cursor-not-allowed disabled:opacity-40'}
        title="复制链接"
      >
        <ExternalLink className="h-4 w-4" />
        {mobile && '复制链接'}
      </button>
      <button
        onClick={() => void onDelete([item.key])}
        disabled={deleting}
        className={mobile
          ? 'inline-flex items-center justify-center gap-1 rounded-lg border border-red-200 px-3 py-2 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40'
          : 'rounded-lg p-2 text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40'}
        title="删除对象"
      >
        <Trash2 className="h-4 w-4" />
        {mobile && '删除'}
      </button>
    </div>
  );
}
